import React from "react"
import { FormattedDate, FormattedMessage } from "react-intl"
import { Link } from "react-router-dom"
import { SelectOption } from "../../common/types"
import { FilterStatusOption, INVENTORY_STOCK_DOCUMENT_DEFINITION_TABS, StockImportFormAttributes } from "../../common/types/inventory"
import ImportStatusComponent from "../../inventory/components/ImportStatusComponent"
import { NormalizedStockDocumentDefinition } from "../../state/inventory"
import { INVENTORY_COST_METHOD } from "../../state/inventory/configuration/models"
import { InventoryLocationQuantities } from "../../state/inventory/data/model/inventoryData"
import { ImportStockDtoAttributes } from "../../state/inventory/imports/models"
import { ImportElastic } from "../../state/inventory/imports/service/types"
import { NormalizedLocation } from "../../state/inventory/location/reducers"
import Location from "../../state/location/Location"
import { PRODUCT_TYPE } from "../../state/product/Types"

export const COST_METHOD_OPTIONS = [
    {
        id: INVENTORY_COST_METHOD.FIFO,
        name: <FormattedMessage
            id="inventory.components.costMethodSelector.fifo_name"
            defaultMessage="First in First Out"
        />,
        icon: <FormattedMessage
            id="inventory.components.costMethodSelector.fifo_icon"
            defaultMessage="FIFO"
        />,
        description: <>
            <h6 className={"mt-2 mb-3"}>
                <FormattedMessage
                    id="inventory.components.costMethodSelector.fifo_description_header"
                    defaultMessage="First-In-First-Out (FIFO)"
                />
            </h6>
            <FormattedMessage
                id="inventory.components.costMethodSelector.fifo_description"
                defaultMessage="By selecting the First-In-First-Out (FIFO) method of inventory valuation the products which
            are purchased from the supplier (or produced) first will be sold first."
            /></>,
    },
    {
        id: INVENTORY_COST_METHOD.LIFO,
        name: <FormattedMessage
            id="inventory.components.costMethodSelector.lifo_name"
            defaultMessage="Last in First Out"
        />,
        icon: <FormattedMessage
            id="inventory.components.costMethodSelector.lifo_icon"
            defaultMessage="LIFO"
        />,
        description: <>
            <h6 className={"mt-2 mb-3"}>
                <FormattedMessage
                    id="inventory.components.costMethodSelector.lifo_description_header"
                    defaultMessage="Last-In-First-Out (LIFO)"
                />
            </h6>
            <FormattedMessage
                id="inventory.components.costMethodSelector.lifo_description"
                defaultMessage="By selecting the Last-In-First-Out (LIFO) method of inventory valuation the products which
            are purchased from the supplier (or produced) last will be sold first."
            />
        </>,
    },
    {
        id: INVENTORY_COST_METHOD.AVERAGE,
        name: <FormattedMessage
            id="inventory.components.costMethodSelector.average_name"
            defaultMessage="Weighted Average Cost"
        />,
        icon: <FormattedMessage
            id="inventory.components.costMethodSelector.average_icon"
            defaultMessage="WAC"
        />,
        description: <>
            <h6 className={"mt-2 mb-3"}>
                <FormattedMessage
                    id="inventory.components.costMethodSelector.average_description_header"
                    defaultMessage="Weighted Average Cost (WAC)"
                />
            </h6>
            <FormattedMessage
                id="inventory.components.costMethodSelector.average_description"
                defaultMessage="By selecting the Weighted Average Cost (WAC) method of inventory valuation of every item
            sold is the weighted average of all the units in stock."
            />
        </>,
    }
]

export const getStockDocumentDefinitionSelection = (listing: NormalizedStockDocumentDefinition[]): Array<SelectOption<string>> => {
    return listing.map((item) => {
        return {
            value: item.id,
            label: item.name
        }
    })
}


export const STOCK_DOCUMENT_DEFINITION_EDIT_TABS = [
    {
        eventKey: INVENTORY_STOCK_DOCUMENT_DEFINITION_TABS.DESCRIPTION,
        title: <FormattedMessage
            id="inventory.sections.stockDocumentDefinitionEdit.tabs_description"
            defaultMessage="Description"
        />,
    },
    {
        eventKey: INVENTORY_STOCK_DOCUMENT_DEFINITION_TABS.STATUS,
        title: <FormattedMessage
            id="inventory.sections.stockDocumentDefinitionEdit.tabs_status"
            defaultMessage="Statuses"
        />,
    },
    {
        eventKey: INVENTORY_STOCK_DOCUMENT_DEFINITION_TABS.TEMPLATE,
        title: <FormattedMessage
            id="inventory.sections.stockDocumentDefinitionEdit.tabs_template"
            defaultMessage="Templates"
        />,
    },
]

export const STOCK_DOCUMENT_DEFINITION_DESCRIPTION_INPUTS = [
    "name",
    "prefix",
]

export const STOCK_DOCUMENT_DEFINITION_STATUS_HEADERS = [
    <FormattedMessage
        key="inventory.sections.stockDocumentDefinitionStatusSection.statuses_header_name"
        id="inventory.sections.stockDocumentDefinitionStatusSection.statuses_header_name"
        defaultMessage="Name"
    />,
    <FormattedMessage
        key="inventory.sections.stockDocumentDefinitionStatusSection.statuses_header_status"
        id="inventory.sections.stockDocumentDefinitionStatusSection.statuses_header_status"
        defaultMessage="Status"
    />,
]

export const mapAvailableLocationsToOptions = (allLocations: Location[], inventoryLocations: NormalizedLocation[]): SelectOption<string>[] => {
    const enabledLocations = allLocations.filter((location) => location.getEnabled())
    const usedLocations: string[] = inventoryLocations.reduce<string[]>((acc, location) => {
        return acc.concat(location.locationIds)
    }, [])
    const availableLocations = enabledLocations.filter(location => !usedLocations.includes(location.getId()))
    const availableOptions = availableLocations.map((location) => ({
        value: location.getId(),
        label: location.getName()
    }))
    return availableOptions
}


export const mapToRows = (location: NormalizedLocation) => {

    const name =
        <Link to={`/inventory/stock-locations/${location.id}/edit`}>
            <p key={"title" + location.id} className="m-0">
                {location.name}
            </p>
        </Link>

    return {
        rowId: location?.id,
        data: [
            name,
            location?.locations?.map(loc => loc.attributes.name).join(", ")
        ],
    }
}

export const listingHeaders = () => {
    return [
        <FormattedMessage
            key="name"
            id="inventory.stock_locations.list.headers.name"
            defaultMessage="Name"
        />,
        <FormattedMessage
            key="locations"
            id="inventory.stock_locations.list.headers.locations_assigned"
            defaultMessage="Locations assigned"
        />,
    ]
}
export const createStockImportDto = (form: StockImportFormAttributes): ImportStockDtoAttributes => {
    const newImport: ImportStockDtoAttributes = {
        inventoryLocationId: form.inventoryLocation,
        importType: form.importType,
        notes: form.notes,
        fileId: form.file,
        type: "csv"
    }
    return newImport
}

export const createStockImportFromIntegrationDto = (form: StockImportFormAttributes): ImportStockDtoAttributes => {
    const newImport: ImportStockDtoAttributes = {
        importType: form.importType,
        notes: form.notes,
        type: form.importFrom,
        integration: form.integration,
    }
    return newImport
}

export const HEADER_KEYS_FIELDS = {
    "id": "identifierKeyword", "createdAt": "createdAt", "inventoryLocationId": "inventoryLocationId"
    , "importType": "type", "status": "status"
}

export const mapStockImportsToRows = (item: ImportElastic) => {
    return {
        rowId: item?.id,
        data: [
            <Link to={"/inventory/imports/" + item.id + "/edit"} key={`${item.id}-importIdentifier`}>
                {item.identifier ? item.identifier : item.id}
            </Link>,
            <FormattedDate
                key={`${item.id}-createdAt`}
                year="numeric"
                month="long"
                day="numeric"
                value={new Date(item.createdAt)}
            />,
            item?.inventoryLocation?.attributes.name ?? "",
            item?.type ?? "",
            <ImportStatusComponent key={`${item.id}-${item.status}`} status={item?.status}/>,
        ],
    }
}

export const inventoryStockDocumentHeaders = [
    <FormattedMessage
        key="id"
        id="inventory.stock_document.headers.id"
        defaultMessage="ID"
    />,
    <FormattedMessage
        key="date"
        id="inventory.stock_document.headers.date"
        defaultMessage="Date"
    />,
    <FormattedMessage
        key="contact"
        id="inventory.stock_document.headers.supplier"
        defaultMessage="Supplier"
    />,
    <FormattedMessage
        key="status"
        id="inventory.stock_document.headers.status"
        defaultMessage="Status"
    />,
    <FormattedMessage
        key="amount"
        id="inventory.stock_document.headers.amount"
        defaultMessage="Amount"
    />,
]

export const AddProductsFromOpenOrdersHeaders = [
    <FormattedMessage
        key="product"
        id="inventory.stock_document.add_products_from_open_orders.headers.product"
        defaultMessage="Product"
    />,
    <FormattedMessage
        key="order"
        id="inventory.stock_document.add_products_from_open_orders.headers.order"
        defaultMessage="Order"
    />,
    <FormattedMessage
        key="contact"
        id="inventory.stock_document.add_products_from_open_orders.headers.contact"
        defaultMessage="Contact"
    />,
    <FormattedMessage
        key="quantity"
        id="inventory.stock_document.add_products_from_open_orders.headers.quantity"
        defaultMessage="Quantity requested"
    />,
]

export const PRODUCT_STOCK_QUANTITIES_HEADERS = [
    {
        key: "inventory.modals.product_stock_quantities_modal.document",
        id: "inventory.modals.product_stock_quantities_modal.document",
        defaultMessage: "Document"
    },
    {
        key: "inventory.modals.product_stock_quantities_modal.date",
        id: "inventory.modals.product_stock_quantities_modal.date",
        defaultMessage: "Date"
    },
    {
        key: "inventory.modals.product_stock_quantities_modal.contact",
        id: "inventory.modals.product_stock_quantities_modal.contact",
        defaultMessage: "Contact"
    },
    {
        key: "inventory.modals.product_stock_quantities_modal.quantity",
        id: "inventory.modals.product_stock_quantities_modal.quantity",
        defaultMessage: "Quantity"
    },
    
]

export const getQuantityAvailableByInventoryLocationId = (product, inventoryLocationId) => {
    const productInventoryQuantities = product.attributes?.inventoryLocationQuantities || product.inventoryLocationQuantities
    const productInventoryQuantity = productInventoryQuantities?.find(loc => loc.attributes?.locationId === inventoryLocationId)
        || productInventoryQuantities?.find(loc => loc.locationId === inventoryLocationId)
    const qoh = productInventoryQuantity ? (productInventoryQuantity.attributes?.quantityOnHand || productInventoryQuantity.quantityOnHand) : 0
    const qr = productInventoryQuantity ? (productInventoryQuantity.attributes?.quantityReserved || productInventoryQuantity.quantityReserved) : 0
    return parseInt(qoh) - parseInt(qr)
}

export const getRemoteQuantityAvailableByInventoryLocationId= (product, inventoryLocationIdToExclude) => {
    const inventoryLocationQuantities = product?.inventoryLocationQuantities || []
    let otherLocations
    if(inventoryLocationIdToExclude){
        otherLocations = inventoryLocationQuantities.filter(loc => loc.attributes?.locationId !== inventoryLocationIdToExclude)
        || inventoryLocationQuantities.filter(loc => loc.locationId !== inventoryLocationIdToExclude)
        if (otherLocations.length === 0) {
            return 0
        }
    }else {
        otherLocations = inventoryLocationQuantities
    }
    
    if (otherLocations.length === 0) {
        return 0
    }
    const qoh = otherLocations.reduce( (accumulator, item) => accumulator + parseInt(item.quantityOnHand||item.attributes?.quantityOnHand), 0)
    const qr = otherLocations.reduce( (accumulator, item) => accumulator + parseInt(item.quantityReserved ||item.attributes?.quantityReserved), 0)
    const qa = parseInt(qoh) - parseInt(qr)
    return isNaN(qa)? 0 : qa
}

export const getInventoryLocationFromLocation = (locationId, listings: NormalizedLocation[]) => {
    if (listings.length > 0 ){
        const foundLocation = listings.find(listing =>
            listing.locationIds?.some(id => id === locationId)
        )
        return foundLocation? foundLocation : null
    }
    return null
}

export const getProductQuantities = (product) => {
    let productQuantities: InventoryLocationQuantities[] = []
    const prodType = product?.attributes 
        ? product?.attributes?.type 
        : product?.type
    if (prodType != PRODUCT_TYPE.MATRIX_PARENT) {
        productQuantities = product.attributes.inventoryLocationQuantities
    } else {
        const matrixProd = product?.attributes ? product?.attributes.matrix : product?.matrix
        for (let i = 0; i < matrixProd.attributes.items.length; i++){
            const elem = matrixProd.attributes.items[i]
            if (elem) {
                const newLocQtyArr: InventoryLocationQuantities[] = elem.attributes.product?.attributes.inventoryLocationQuantities.map(
                    (q) => {
                        q.attributes.variantId = elem.attributes.productId
                        q.attributes.variantName = elem.attributes.product?.attributes.title
                        return q
                    })
                productQuantities = productQuantities.concat(newLocQtyArr)
            }
        }
    }
    return productQuantities
}

export const sortStatusOptions = (status: FilterStatusOption[]) => {
    return status?.slice().sort((a, b) => {
        const labelA = a.label.toUpperCase()
        const labelB = b.label.toUpperCase()
        if (labelA < labelB) {
            return -1
        }
        if (labelA > labelB) {
            return 1
        }
        return 0
    }) || []
}


// WEBPACK FOOTER //
// ./src/utils/inventory/utils.tsx